import React, { useState } from 'react';
import laboratoryInfo from '../config';
import { useData } from '../contexts/DataContext';
import { getLotNumbers, getApplicableStandardsText } from '../data/materialLotData';
import { FaUserInjured } from 'react-icons/fa';
import DocumentPreview from './DocumentPreview';

const PatientSheetGenerator = ({ order, showButton = true, buttonLabel = "Fiche Patient" }) => {
    const { products, clients } = useData();
    const practitionerClient = clients.find(c => c.id === (order?.clientId || order?.client_id));
    const [showPreview, setShowPreview] = useState(false);

    const items = [];
    const currentOrder = order || {};
    const currentId = currentOrder.id || 'preview';
    const targetId = `patientsheet-img-${currentId}`;
    const fileName = `fiche-patient-${currentOrder.patientName || 'travail'}`;

    if (currentOrder.teethConfig) {
        if (Array.isArray(currentOrder.teethConfig)) {
            // New Array Format
            currentOrder.teethConfig.forEach(line => {
                const { productId, teeth, details } = line;
                const product = products.find(p => p.id === productId);

                if (product) {
                    items.push({
                        toothId: teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', '),
                        category: product.category,
                        name: product.name,
                        quantity: line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
                            ? Number(line.quantity) 
                            : (teeth.length || 1),
                        details: details || {},
                        notes: line.notes
                    });
                }
            });
        } else {
            // Legacy Object Format
            Object.entries(currentOrder.teethConfig).forEach(([toothId, config]) => {
                const { productId, details } = config;
                const product = products.find(p => p.id === productId);

                if (product) {
                    items.push({
                        toothId,
                        category: product.category,
                        name: product.name,
                        quantity: 1,
                        details: details || {},
                        notes: config.notes
                    });
                }
            });
        }
    }

    // Calcul précis des lots et des normes applicables
    const detectedLots = [];
    const seenLotLabels = new Set();
    items.forEach(item => {
        const lots = getLotNumbers(item.category, item.name, item.details?.teinte, item.notes);
        lots.forEach(l => {
            if (!seenLotLabels.has(l.label)) {
                seenLotLabels.add(l.label);
                detectedLots.push(l);
            }
        });
    });

    return (
        <>
            {showButton && (
                <button
                    className="btn btn-sm btn-outline-success"
                    onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
                    title="Générer Fiche Patient"
                >
                    <FaUserInjured className={buttonLabel ? "me-1" : ""} /> {buttonLabel}
                </button>
            )}

            {showPreview && (
                <DocumentPreview
                    targetId={targetId}
                    fileName={fileName}
                    onClose={() => setShowPreview(false)}
                />
            )}

            {/* Hidden Template for Image Capture */}
            <div id={targetId} style={{ display: 'none' }}>
                <div style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '3px solid #166534', paddingBottom: '15px' }}>
                        <div>
                            <h2 style={{ margin: '0', color: '#166534' }}>{laboratoryInfo.name}</h2>
                            <p style={{ margin: '5px 0', fontSize: '13px', color: '#4ade80', fontWeight: 'bold' }}>LABORATOIRE DE PROTHÈSE DENTAIRE</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h1 style={{ margin: '0', fontSize: '24px', color: '#166534' }}>FICHE PATIENT</h1>
                            <p style={{ margin: '5px 0', fontWeight: 'bold', fontSize: '14px' }}>N° DOSSIER: {currentOrder.caseNumber || (typeof currentId === 'string' ? currentId.substring(0, 8).toUpperCase() : 'PREVIEW')}</p>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ border: '1px solid #bbf7d0', padding: '15px', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', backgroundColor: '#f0fdf4', padding: '8px', fontSize: '14px', borderRadius: '4px', color: '#166534' }}>PRATICIEN</h4>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Nom:</strong> {currentOrder.practitionerName}</p>
                            {practitionerClient?.address && <p style={{ margin: '5px 0', fontSize: '13px', color: '#475569' }}><strong>Adresse:</strong> {practitionerClient.address}</p>}
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Date commande:</strong> {new Date(currentOrder.createdAt || new Date()).toLocaleDateString()}</p>
                        </div>
                        <div style={{ border: '1px solid #bbf7d0', padding: '15px', borderRadius: '8px' }}>
                            <h4 style={{ margin: '0 0 10px 0', backgroundColor: '#f0fdf4', padding: '8px', fontSize: '14px', borderRadius: '4px', color: '#166534' }}>PATIENT</h4>
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Nom:</strong> {currentOrder.patientName}</p>
                            {(currentOrder.patientReference || currentOrder.patient_reference) && (
                                <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Référence :</strong> {currentOrder.patientReference || currentOrder.patient_reference}</p>
                            )}
                            <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Gamme:</strong> {currentOrder.gamme ? (currentOrder.gamme.toLowerCase() === 'nova' ? 'FINITION NOVA' : currentOrder.gamme.toUpperCase()) : 'FINITION NOVA'}</p>
                        </div>
                    </div>

                    <h4 style={{ backgroundColor: '#166534', color: '#fff', padding: '10px', margin: '20px 0 10px 0', borderRadius: '4px', fontSize: '15px' }}>DÉTAILS TECHNIQUES</h4>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #166534' }}>
                                <th style={{ textAlign: 'center', padding: '12px 4px', width: '140px', color: '#166534' }}>DENT</th>
                                <th style={{ textAlign: 'left', padding: '12px', color: '#166534' }}>TRAVAIL À RÉALISER</th>
                                <th style={{ textAlign: 'center', padding: '12px', width: '80px', color: '#166534' }}>QUANTITÉ</th>
                                <th style={{ textAlign: 'left', padding: '12px', color: '#166534' }}>TEINTE / NOTES</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f0fdf4' }}>
                                    <td style={{ textAlign: 'center', padding: '15px 4px', fontWeight: 'bold', fontSize: '14px', color: '#166534', lineHeight: '1.4' }}>{item.toothId}</td>
                                    <td style={{ padding: '15px' }}>
                                        <div style={{ fontWeight: 'bold', color: '#334155' }}>{item.category}</div>
                                        <div style={{ fontSize: '14px', color: '#475569' }}>{item.name}</div>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '15px', fontSize: '15px', fontWeight: 'bold' }}>{item.quantity}</td>
                                    <td style={{ padding: '15px' }}>
                                        {item.details.teinte && <div style={{ fontSize: '14px' }}><strong>Teinte:</strong> {item.details.teinte}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Notes & Remarques Section */}
                    {currentOrder.notes && currentOrder.notes.trim() !== '' && (
                        <div style={{ marginBottom: '20px', border: '1px solid #bbf7d0', padding: '15px', borderRadius: '8px', backgroundColor: '#f0fdf4' }}>
                            <h4 style={{ margin: '0 0 10px 0', borderBottom: '1px solid #bbf7d0', paddingBottom: '8px', fontSize: '14px', color: '#166534' }}>NOTES & REMARQUES</h4>
                            <div style={{ fontSize: '13px', color: '#14532d', backgroundColor: '#ffffff', padding: '8px', borderRadius: '4px', borderLeft: '3px solid #22c55e', whiteSpace: 'pre-wrap' }}>
                                <strong>Remarque générale :</strong> {currentOrder.notes}
                            </div>
                        </div>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                        <div style={{ border: '1px solid #bbf7d0', padding: '12px 15px', borderRadius: '8px', backgroundColor: '#f0fdf4' }}>
                            <h4 style={{ margin: '0 0 8px 0', borderBottom: '1px solid #bbf7d0', paddingBottom: '6px', fontSize: '13px', color: '#166534' }}>TRAÇABILITÉ DES MATÉRIAUX & NORMES ISO / UE</h4>
                            <div style={{ fontSize: '11px', color: '#334155', lineHeight: '1.8' }}>
                                {detectedLots.length > 0 ? (
                                    detectedLots.map((lot, idx) => (
                                        <div key={idx}><strong>{lot.label} :</strong> {lot.value}</div>
                                    ))
                                ) : (
                                    <>
                                        Lot Disque Zircone / Métal Co-Cr : ___________________________<br />
                                        Lot Résine / Porcelaine : ___________________________<br />
                                        Lot Dents résine / Titane : ___________________________<br />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Déclaration d'engagement et de conformité DM sur mesure */}
                    <div style={{ marginBottom: '25px', border: '1.5px solid #86efac', padding: '12px 15px', borderRadius: '8px', backgroundColor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#166534', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            ⚖️ DÉCLARATION DE CONFORMITÉ — DISPOSITIF MÉDICAL SUR MESURE (DMSM)
                        </h4>
                        <p style={{ margin: '0', fontSize: '10.5px', color: '#334155', lineHeight: '1.5', textAlign: 'justify' }}>
                            Le laboratoire <strong>{laboratoryInfo.name}</strong> certifie sur l’honneur que ce dispositif médical sur mesure a été conçu et fabriqué exclusivement pour le patient susnommé, conformément à la prescription médicale du praticien et répond aux exigences générales de sécurité et de performances de l’annexe I et de l'annexe XIII du <strong>Règlement Européen (UE) 2017/745 (MDR)</strong> relatif aux dispositifs médicaux, ainsi qu'{getApplicableStandardsText(detectedLots)}
                        </p>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '30px', borderTop: '1px solid #bbf7d0', paddingTop: '15px' }}>
                        <div style={{ textAlign: 'center', border: '1px solid #16a34a', borderRadius: '8px', padding: '8px 16px', fontSize: '11px', color: '#166534', minWidth: '160px' }}>
                            <strong>Contrôle Qualité de Libération</strong>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40px', marginTop: '4px' }}>
                                <img src="/signature.png" alt="Signature" style={{ height: '50px', maxHeight: '50px', objectFit: 'contain' }} />
                            </div>
                        </div>
                    </div>

                    <div style={{
                        marginTop: '25px',
                        textAlign: 'center',
                        fontSize: '10px',
                        color: '#166534',
                        borderTop: '1px solid #bbf7d0',
                        paddingTop: '10px'
                    }}>
                        <p style={{ fontWeight: 'bold', margin: '0' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PatientSheetGenerator;
